import { useMemo, useState, type FunctionComponent } from "react";
import { LockOutlined, PlusOutlined, RobotOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  message,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import groupBy from "lodash.groupby";
import isEmpty from "lodash.isempty";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { productAutomationActions, RequestStatus } from "@fitness-app/app-store";
import {
  AutomationStartPoint,
  CheckoutVariantFilterType,
  DiscordIntegrationTask,
  EmailClientTask,
  FitnessAppTask,
  PriceFilterType,
  ProductTask,
  TaskCreator,
  TaskType,
  type AddClientToFitnessApp,
  type AddFieldValues,
  type AddTagsTask,
  type AddToListsTask,
  type AutomationTask,
  type CheckoutVariantFilter,
  type EmailTaskGroup,
  type FieldValuesEmailClient,
  type FieldValuesEmailList,
  type PriceFilter,
  type RemoveFromListsTask,
  type RemoveTagsTask,
  type TagsEmailClient,
  type TagsEmailList,
} from "@fitness-app/data-models/entities/Automation";
import { EmailClient } from "@fitness-app/data-models/entities/ProductsSettings";

import ModalForm from "~/components/ModalForm/ModalForm";
import { LIST_ITEM_SEPARATOR } from "~/constants/separators";
import { useEntityChange } from "~/hooks/useEntityChange";
import { useFitnessAppAutomationTask } from "~/modules/Products/hooks/useFitnessAppAutomationTask";
import { useProductPricesOptions } from "~/modules/Products/hooks/useProductPricesOptions";
import { useProductTaskAutomation } from "~/modules/Products/hooks/useProductTaskAutomation";
import AutomationTaskForm from "~/modules/Products/Product/ProductAutomation/components/AutomationTaskForm/AutomationTaskForm";
import {
  type AddClientToFitnessApp as AddClientToFitnessAppFormModel,
  type AutomationTaskFormModel,
} from "~/modules/Products/Product/ProductAutomation/components/AutomationTaskForm/types";
import {
  AutomationTaskDetails,
  AutomationTaskSummary,
  CATEGORY_META,
  CATEGORY_ORDER,
  describeAutomationTask,
  getTaskCategory,
  getTaskSearchText,
  getTaskTitle,
  type DescribeContext,
  type TaskCategory,
} from "~/modules/Products/Product/ProductAutomation/components/AutomationTasks/automationTaskSummary";
import { useDiscordIntegration } from "~/modules/Products/Product/ProductAutomation/hooks/useDiscordIntegration";
import { useCheckoutVariants } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariants";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  productId: string;
}

type Props = OwnProps;

const AutomationTasks: FunctionComponent<Props> = ({ productId }) => {
  const { t } = useTranslation(["products", "common"]);
  const { data, status, error } = useAppSelector((store) => store.productAutomation);
  const [showModal, toggleModal] = useState(false);
  const dispatch = useAppDispatch();
  const productDetails = useAppSelector((store) => store.product.details);
  const [model, setModel] = useState<null | (AutomationTaskFormModel & { id: string })>(null);
  const [processedId, setProcessedId] = useState<null | string>(null);
  const { programAutomationOptions } = useFitnessAppAutomationTask(productDetails);
  const [loading, onSuccess, onFailure, onStart] = useEntityChange(() => {
    toggleModal(false);
    setModel(null);
  });
  const [removing, onRemoveSuccess, onRemoveFailure, onStartRemoving] = useEntityChange();

  const { productsById } = useProductTaskAutomation(productId);
  const { discordIntegration } = useDiscordIntegration();
  const { data: checkoutVariants } = useCheckoutVariants(productId);
  const productPricesOptions = useProductPricesOptions();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<TaskCategory | "all">("all");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [onlyActive, setOnlyActive] = useState(false);

  const describeContext = useMemo<DescribeContext>(
    () => ({
      t,
      productsById,
      pricesById: Object.fromEntries((productPricesOptions ?? []).map((price) => [price.value, price.label])),
      checkoutVariantsById: Object.fromEntries((checkoutVariants ?? []).map((variant) => [variant.id, variant.slug])),
      discordServersById: Object.fromEntries(
        (discordIntegration ?? []).map((server) => [server.guildId, server.guild.name]),
      ),
    }),
    [t, productsById, productPricesOptions, checkoutVariants, discordIntegration],
  );

  const tasks = data?.tasks ?? [];

  const eventOptions = useMemo(() => {
    const events = new Set<string>();
    tasks.forEach((task) => task.triggers?.forEach((event) => events.add(event)));
    return Array.from(events).map((event) => ({ value: event, label: t(`common:${event}`) }));
  }, [tasks, t]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<TaskCategory, number>();
    tasks.forEach((task) => {
      const cat = getTaskCategory(task);
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    });
    return counts;
  }, [tasks]);

  const categoryOptions = useMemo(
    () => [
      { label: `${t("automationTasks.filters.allCategories")} (${tasks.length})`, value: "all" },
      ...CATEGORY_ORDER.filter((cat) => categoryCounts.get(cat)).map((cat) => ({
        label: `${t(`automationTasks.category.${cat}`)} (${categoryCounts.get(cat) ?? 0})`,
        value: cat,
      })),
    ],
    [categoryCounts, tasks.length, t],
  );

  const filteredTasks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tasks.filter((task) => {
      if (onlyActive && !task.active) {
        return false;
      }
      if (category !== "all" && getTaskCategory(task) !== category) {
        return false;
      }
      if (selectedEvents.length && !task.triggers?.some((event) => selectedEvents.includes(event))) {
        return false;
      }
      if (query) {
        const description = describeAutomationTask(task, describeContext);
        const eventLabels = (task.triggers ?? []).map((event) => t(`common:${event}`));
        const text = getTaskSearchText(description, getTaskTitle(task, t), [task.description ?? "", ...eventLabels]);
        if (!text.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [tasks, search, category, selectedEvents, onlyActive, describeContext, t]);

  const handleTaskRemove = (task: AutomationTask) => {
    onStartRemoving(task.id);
    void dispatch(
      productAutomationActions.updateProductAutomationTask({
        payload: {
          productId,
          task,
          action: "remove",
        },
        onSuccess: onRemoveSuccess,
        onFailure: onRemoveFailure,
      }),
    );
  };

  const handleActiveChange = async (state: boolean, task: AutomationTask) => {
    setProcessedId(task.id);
    await dispatch(
      productAutomationActions.updateProductAutomationTask({
        payload: {
          productId,
          task: {
            ...task,
            active: state,
          },
          action: "update",
        },
      }),
    );
    setProcessedId(null);
  };

  const handleSubmit = (formData: AutomationTaskFormModel) => {
    let action: AutomationTask["action"] | null = null;
    let automationTask: AutomationTask | null = null;

    const filters: AutomationTask["filters"] = [];

    if (formData.automationFilters && formData.includedPrices?.length) {
      filters.push({ type: PriceFilterType.Included, prices: formData.includedPrices });
    }

    if (formData.automationFilters && formData.excludedPrices?.length) {
      filters.push({ type: PriceFilterType.Excluded, prices: formData.excludedPrices });
    }

    if (formData.automationFilters && formData.includedCheckoutVariants?.length) {
      filters.push({ type: CheckoutVariantFilterType.Included, checkoutVariants: formData.includedCheckoutVariants });
    }

    if (formData.automationFilters && formData.excludedCheckoutVariants?.length) {
      filters.push({ type: CheckoutVariantFilterType.Excluded, checkoutVariants: formData.excludedCheckoutVariants });
    }

    const taskBaseData = {
      active: formData.active,
      description: formData.description,
      protected: formData.protected ?? false,
      triggers: formData.triggers,
      creator: formData.creator ?? TaskCreator.Owner,
      filters: filters.length ? filters : null,
    };

    if (formData.action.type === ProductTask.RemoveFromProduct) {
      action = formData.action;

      if (!action.products?.length) {
        void message.warning("Musisz wybrać przynajmniej jeden produkt");
        return;
      }

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.ProductTask,
      };
    }

    if (formData.action.type === ProductTask.ImportIntoProduct) {
      action = formData.action;

      if (!action.products?.length) {
        void message.warning("Musisz wybrać przynajmniej jeden produkt");
        return;
      }

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.ProductTask,
      };
    }

    if (formData.action.type === FitnessAppTask.AddClientToApp) {
      const { startAutomationFrom, ...rest } = formData.action;

      let sendReminderAboutAccess: AddClientToFitnessApp["sendReminderAboutAccess"] = false;

      if (
        "sendReminderAboutAccess" in formData.action &&
        formData.action.sendReminderAboutAccess &&
        !isEmpty(formData.action.sendReminderAboutAccess)
      ) {
        sendReminderAboutAccess = formData.action.sendReminderAboutAccess.map((item) => {
          return {
            ...item,
            actions: item.actions.map((action) => {
              if (action.type === EmailClientTask.AddTags || action.type === EmailClientTask.RemoveTags) {
                const lists: (TagsEmailList | TagsEmailClient)[] = action.emailClients.map((listStr) => {
                  const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
                  if (id && name) {
                    return {
                      client: client as EmailClient,
                      account,
                      id,
                      name,
                    } as TagsEmailList;
                  }
                  return {
                    client: client as EmailClient,
                    account,
                  } as TagsEmailClient;
                });
                const groupByClient = groupBy(lists, "client");

                if (groupByClient[EmailClient.GetResponse] && groupByClient[EmailClient.GetResponse].length > 1) {
                  void message.warning("getResponseListWarning");
                  return;
                }

                return {
                  ...action,
                  emailClients: lists,
                } as AddTagsTask | RemoveTagsTask;
              }

              if (action.type === EmailClientTask.AddToLists || action.type === EmailClientTask.RemoveFromLists) {
                const lists = action.emailLists.map((listStr) => {
                  const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
                  return {
                    client: client as EmailClient,
                    account: account || "",
                    id: id || "",
                    name: name || "",
                  };
                });
                const groupByClient = groupBy(lists, "client");

                if (groupByClient[EmailClient.GetResponse] && groupByClient[EmailClient.GetResponse].length > 1) {
                  void message.warning("getResponseListWarning");
                  return;
                }

                return {
                  ...action,
                  emailLists: lists,
                } as AddToListsTask | RemoveFromListsTask;
              }

              const lists: (FieldValuesEmailList | FieldValuesEmailClient)[] = action.emailLists.map((listStr) => {
                const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
                if (id && name) {
                  return {
                    client: client as EmailClient,
                    account,
                    id,
                    name,
                  } as FieldValuesEmailList;
                }
                return {
                  client: client as EmailClient,
                  account,
                } as FieldValuesEmailClient;
              });
              const groupByClient = groupBy(lists, "client");

              if (groupByClient[EmailClient.GetResponse] && groupByClient[EmailClient.GetResponse].length > 1) {
                void message.warning("getResponseListWarning");
                return;
              }

              return {
                ...action,
                emailLists: lists,
                values: action.values ? Object.fromEntries(action.values?.map((item) => [item.key, item.value])) : {},
              } as AddFieldValues;
            }) as EmailTaskGroup[],
          };
        });
      }

      action = {
        ...rest,
        tags: formData.action.tags || [],
        programAutomation: formData.action.programAutomation
          ? {
              name:
                programAutomationOptions.find(
                  (item) =>
                    formData.action.type === FitnessAppTask.AddClientToApp &&
                    item.value === formData.action.programAutomation,
                )?.label ?? "",
              id: formData.action.programAutomation,
              startAutomationFrom: formData.action.startAutomationFrom || AutomationStartPoint.AfterPurchase,
            }
          : null,
        sendReminderAboutAccess,
      };

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.FitnessApp,
      };
    }

    if (
      formData.action.type === FitnessAppTask.AddTagsToTraineeProfile ||
      formData.action.type === FitnessAppTask.RemoveTagsFromTraineeProfile
    ) {
      action = formData.action;

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.FitnessApp,
      };
    }

    if (
      formData.action.type === FitnessAppTask.ActivatedCustomerFeature ||
      formData.action.type === FitnessAppTask.DeactivatedCustomerFeature
    ) {
      action = formData.action;
      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.FitnessApp,
      };
    }

    if (formData.action.type === FitnessAppTask.AddProgramAutomationToClient) {
      const { startAutomationFrom, ...rest } = formData.action;

      action = {
        ...rest,
        programAutomation: {
          name:
            programAutomationOptions.find(
              (item) =>
                formData.action.type === FitnessAppTask.AddClientToApp &&
                item.value === formData.action.programAutomation,
            )?.label ?? "",
          id: formData.action.programAutomation,
          startAutomationFrom: formData.action.startAutomationFrom || AutomationStartPoint.AfterPurchase,
        },
      };

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.FitnessApp,
      };
    }

    if (formData.action.type === FitnessAppTask.RemoveClientFromApp) {
      action = formData.action;

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.FitnessApp,
      };
    }

    if (
      formData.action.type === DiscordIntegrationTask.AddToServer ||
      formData.action.type === DiscordIntegrationTask.RemoveFromServer
    ) {
      action = formData.action;

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.DiscordIntegration,
      };
    }

    if (formData.action.type === EmailClientTask.AddTags || formData.action.type === EmailClientTask.RemoveTags) {
      const lists: (TagsEmailList | TagsEmailClient)[] = formData.action.emailClients.map((listStr) => {
        const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
        if (id && name) {
          return {
            client: client as EmailClient,
            account,
            id,
            name,
          } as TagsEmailList;
        }
        return {
          client: client as EmailClient,
          account,
        } as TagsEmailClient;
      });
      const groupByClient = groupBy(lists, "client");

      if (groupByClient[EmailClient.GetResponse] && groupByClient[EmailClient.GetResponse].length > 1) {
        void message.warning("getResponseListWarning");
        return;
      }

      action = {
        ...formData.action,
        emailClients: lists,
      };

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.EmailClient,
      };
    }

    if (
      formData.action.type === EmailClientTask.AddToLists ||
      formData.action.type === EmailClientTask.RemoveFromLists
    ) {
      const lists = formData.action.emailLists.map((listStr) => {
        const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
        return {
          client: client as EmailClient,
          account: account || "",
          id: id || "",
          name: name || "",
        };
      });
      const groupByClient = groupBy(lists, "client");

      if (groupByClient[EmailClient.GetResponse] && groupByClient[EmailClient.GetResponse].length > 1) {
        void message.warning("getResponseListWarning");
        return;
      }

      action = {
        ...formData.action,
        emailLists: lists,
      };

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.EmailClient,
      };
    }

    if (formData.action.type === EmailClientTask.AddFieldValues) {
      const lists: (FieldValuesEmailList | FieldValuesEmailClient)[] = formData.action.emailLists.map((listStr) => {
        const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
        if (id && name) {
          return {
            client: client as EmailClient,
            account,
            id,
            name,
          } as FieldValuesEmailList;
        }
        return {
          client: client as EmailClient,
          account,
        } as FieldValuesEmailClient;
      });
      const groupByClient = groupBy(lists, "client");

      if (groupByClient[EmailClient.GetResponse] && groupByClient[EmailClient.GetResponse].length > 1) {
        void message.warning("getResponseListWarning");
        return;
      }

      action = {
        ...formData.action,
        emailLists: lists,
        values: formData.action.values
          ? Object.fromEntries(formData.action.values?.map((item) => [item.key, item.value]))
          : {},
      };

      automationTask = {
        ...taskBaseData,
        action,
        id: model?.id || uuid(),
        type: TaskType.EmailClient,
      };
    }

    if (!action || !automationTask) {
      return onFailure();
    }

    onStart();
    void dispatch(
      productAutomationActions.updateProductAutomationTask({
        payload: {
          productId,
          task: automationTask,
          action: model?.id ? "update" : "add",
        },
        onSuccess,
        onFailure,
      }),
    );
  };

  const handleEdit = (task: AutomationTask) => {
    let action: AutomationTaskFormModel["action"] | null = null;

    if (
      task.action.type === DiscordIntegrationTask.AddToServer ||
      task.action.type === DiscordIntegrationTask.RemoveFromServer
    ) {
      action = {
        ...task.action,
      };
    }

    if (
      task.action.type === FitnessAppTask.AddTagsToTraineeProfile ||
      task.action.type === FitnessAppTask.RemoveTagsFromTraineeProfile
    ) {
      action = {
        ...task.action,
      };
    }

    if (
      task.action.type === FitnessAppTask.ActivatedCustomerFeature ||
      task.action.type === FitnessAppTask.DeactivatedCustomerFeature
    ) {
      action = {
        ...task.action,
      };
    }

    if (task.action.type === ProductTask.ImportIntoProduct || task.action.type === ProductTask.RemoveFromProduct) {
      action = {
        ...task.action,
      };
    }

    if (task.action.type === EmailClientTask.AddTags || task.action.type === EmailClientTask.RemoveTags) {
      action = {
        ...task.action,
        emailClients: task.action.emailClients.map((list) =>
          "id" in list
            ? `${list.client}${LIST_ITEM_SEPARATOR}${list.account}${LIST_ITEM_SEPARATOR}${list.id}${LIST_ITEM_SEPARATOR}${list.name}`
            : `${list.client}${LIST_ITEM_SEPARATOR}${list.account}`,
        ),
      };
    }

    if (task.action.type === EmailClientTask.AddToLists || task.action.type === EmailClientTask.RemoveFromLists) {
      action = {
        ...task.action,
        emailLists: task.action.emailLists.map(
          (list) =>
            `${list.client}${LIST_ITEM_SEPARATOR}${list.account}${LIST_ITEM_SEPARATOR}${list.id}${LIST_ITEM_SEPARATOR}${list.name}`,
        ),
      };
    }
    if (task.action.type === EmailClientTask.AddFieldValues) {
      action = {
        ...task.action,
        emailLists: task.action.emailLists.map((list) =>
          "id" in list
            ? `${list.client}${LIST_ITEM_SEPARATOR}${list.account}${LIST_ITEM_SEPARATOR}${list.id}${LIST_ITEM_SEPARATOR}${list.name}`
            : `${list.client}${LIST_ITEM_SEPARATOR}${list.account}`,
        ),
        values: Object.entries(task.action.values).map(([key, value]) => ({
          key,
          value,
        })),
      };
    }

    if (task.action.type === FitnessAppTask.RemoveClientFromApp) {
      action = {
        ...task.action,
      };
    }

    if (task.action.type === FitnessAppTask.AddClientToApp) {
      let sendReminderAboutAccess: AddClientToFitnessAppFormModel["sendReminderAboutAccess"] = [];

      if (
        "sendReminderAboutAccess" in task.action &&
        task.action.sendReminderAboutAccess &&
        task.action.sendReminderAboutAccess.length
      ) {
        sendReminderAboutAccess = task.action.sendReminderAboutAccess.map((item) => {
          return {
            ...item,
            actions: item.actions.map((action) => {
              if (action.type === EmailClientTask.AddTags || action.type === EmailClientTask.RemoveTags) {
                return {
                  ...action,
                  emailClients: action.emailClients.map((list) =>
                    "id" in list
                      ? `${list.client}${LIST_ITEM_SEPARATOR}${list.account}${LIST_ITEM_SEPARATOR}${list.id}${LIST_ITEM_SEPARATOR}${list.name}`
                      : `${list.client}${LIST_ITEM_SEPARATOR}${list.account}`,
                  ),
                };
              }

              if (action.type === EmailClientTask.AddToLists || action.type === EmailClientTask.RemoveFromLists) {
                return {
                  ...action,
                  emailLists: action.emailLists.map(
                    (list) =>
                      `${list.client}${LIST_ITEM_SEPARATOR}${list.account}${LIST_ITEM_SEPARATOR}${list.id}${LIST_ITEM_SEPARATOR}${list.name}`,
                  ),
                };
              }
              return {
                ...action,
                emailLists: action.emailLists.map((list) =>
                  "id" in list
                    ? `${list.client}${LIST_ITEM_SEPARATOR}${list.account}${LIST_ITEM_SEPARATOR}${list.id}${LIST_ITEM_SEPARATOR}${list.name}`
                    : `${list.client}${LIST_ITEM_SEPARATOR}${list.account}`,
                ),
                values: Object.entries(action.values).map(([key, value]) => ({
                  key,
                  value,
                })),
              };
            }),
          };
        });
      }

      action = {
        type: task.action.type,
        programAutomation: task.action.programAutomation?.id || null,
        startAutomationFrom: task.action.programAutomation
          ? task.action.programAutomation.startAutomationFrom
          : undefined,
        tags: task.action.tags,
        accessType: task.action.accessType,
        sendReminderAboutAccess,
      };
    }

    if (task.action.type === FitnessAppTask.AddProgramAutomationToClient) {
      action = {
        type: task.action.type,
        programAutomation: task.action.programAutomation.id,
        startAutomationFrom: task.action.programAutomation
          ? task.action.programAutomation.startAutomationFrom
          : undefined,
      };
    }

    if (!action) {
      void message.warning("Edycja dla tego zadania nie jest obecnie możliwa.");
      return;
    }

    const pricesIncludedFilter = task.filters?.find(
      (filter): filter is PriceFilter => filter.type === PriceFilterType.Included,
    );
    const pricesExcludedFilter = task.filters?.find(
      (filter): filter is PriceFilter => filter.type === PriceFilterType.Excluded,
    );
    const checkoutVariantsIncludedFilter = task.filters?.find(
      (filter): filter is CheckoutVariantFilter => filter.type === CheckoutVariantFilterType.Included,
    );
    const checkoutVariantsExcludedFilter = task.filters?.find(
      (filter): filter is CheckoutVariantFilter => filter.type === CheckoutVariantFilterType.Excluded,
    );

    setModel({
      ...task,
      includedPrices: pricesIncludedFilter?.prices,
      excludedPrices: pricesExcludedFilter?.prices,
      includedCheckoutVariants: checkoutVariantsIncludedFilter?.checkoutVariants,
      excludedCheckoutVariants: checkoutVariantsExcludedFilter?.checkoutVariants,
      automationFilters: !!(
        pricesIncludedFilter ||
        pricesExcludedFilter ||
        checkoutVariantsIncludedFilter ||
        checkoutVariantsExcludedFilter
      ),
      action,
    });

    toggleModal(true);
  };

  const hasTasks = tasks.length > 0;

  return (
    <Card bordered={false} className="!shadow-none" loading={status === RequestStatus.SUBSCRIBING}>
      {error && <Alert closable showIcon message={error} type="error" className="mb-4" />}

      {hasTasks && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder={t("automationTasks.filters.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-xs"
          />
          <Select
            allowClear
            mode="multiple"
            maxTagCount="responsive"
            className="min-w-[220px] flex-1"
            placeholder={t("automationTasks.filters.eventPlaceholder")}
            options={eventOptions}
            value={selectedEvents}
            onChange={setSelectedEvents}
          />
          <Checkbox checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)}>
            {t("automationTasks.filters.onlyActive")}
          </Checkbox>
          <Button className="ml-auto" type="primary" icon={<PlusOutlined />} onClick={() => toggleModal(true)}>
            {t("automationTasks.addTask")}
          </Button>
        </div>
      )}

      {hasTasks && categoryOptions.length > 2 && (
        <div className="mb-3 overflow-x-auto">
          <Segmented
            options={categoryOptions}
            value={category}
            onChange={(value) => setCategory(value as TaskCategory | "all")}
          />
        </div>
      )}

      <Table<AutomationTask>
        pagination={false}
        scroll={{ x: true }}
        rowKey="id"
        expandable={{
          expandedRowRender: (row) => (
            <AutomationTaskDetails description={describeAutomationTask(row, describeContext)} t={t} />
          ),
          rowExpandable: (row) => {
            const description = describeAutomationTask(row, describeContext);
            return Boolean(description.details.length || description.filters.length || description.reminders.length);
          },
        }}
        locale={{
          emptyText: hasTasks ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("automationTasks.filters.noResults")} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("automationTasks.emptyList")}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => toggleModal(true)}>
                {t("automationTasks.addFirstTask")}
              </Button>
            </Empty>
          ),
        }}
        dataSource={filteredTasks}
      >
        <Table.Column<AutomationTask>
          title={t("automationTasks.column.task")}
          dataIndex="task"
          key="task"
          width={460}
          render={(_, row) => {
            const description = describeAutomationTask(row, describeContext);
            const meta = CATEGORY_META[description.category];
            const descriptionText =
              row.creator === TaskCreator.System ? t<string>(row.description ?? "") : row.description;
            return (
              <div className="flex items-start gap-2">
                <Tag color={meta.color} className="mt-0.5 flex h-6 w-6 items-center justify-center !p-0">
                  {meta.icon}
                </Tag>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-gray-800">{getTaskTitle(row, t)}</span>
                    {row.creator === TaskCreator.System && (
                      <Tooltip title={t("automationTasks.summary.systemHint")}>
                        <Tag icon={<RobotOutlined />} className="!m-0 !text-xs">
                          {t("automationTasks.summary.system")}
                        </Tag>
                      </Tooltip>
                    )}
                    {row.protected && (
                      <Tag icon={<LockOutlined />} className="!m-0 !text-xs">
                        {t("automationTasks.summary.protected")}
                      </Tag>
                    )}
                  </div>
                  <AutomationTaskSummary description={description} t={t} />
                  {descriptionText ? (
                    <div className="mt-1 truncate text-xs text-gray-400">{descriptionText}</div>
                  ) : null}
                </div>
              </div>
            );
          }}
        />
        <Table.Column<AutomationTask>
          title={t("automationTasks.column.triggers")}
          dataIndex="triggers"
          key="triggers"
          render={(events: string[]) => (
            <Space direction="vertical" size={2}>
              {events.map((event) => (
                <Tag key={event} className="!m-0">
                  {t(`common:${event}`)}
                </Tag>
              ))}
            </Space>
          )}
        />
        <Table.Column<AutomationTask>
          title={t("automationTasks.column.active")}
          dataIndex="active"
          key="active"
          render={(active: boolean, row) => (
            <Switch
              loading={processedId === row.id}
              checked={active}
              onChange={(state) => handleActiveChange(state, row)}
            />
          )}
        />
        <Table.Column<AutomationTask>
          title={t("automationTasks.column.options")}
          key="action"
          align="left"
          render={(_, row) => (
            <Space>
              <Button type="link" onClick={() => handleEdit(row)}>
                {t("common:button.edit")}
              </Button>
              <Popconfirm
                overlayStyle={{ maxWidth: 400 }}
                placement="leftTop"
                disabled={row.protected}
                title={t("task.deleteWarning")}
                onConfirm={() => handleTaskRemove(row)}
              >
                <Button
                  type="link"
                  danger
                  disabled={row.protected}
                  loading={typeof removing === "string" && removing === row.id}
                >
                  {t("common:button.delete")}
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
      <ModalForm<AutomationTaskFormModel>
        onCancel={() => {
          toggleModal(false);
          setModel(null);
        }}
        visible={showModal}
        title={t("automationTasks.title")}
        width={900}
        loading={!!loading}
      >
        <AutomationTaskForm
          onSubmit={handleSubmit}
          model={
            model
              ? {
                  ...model,
                  description:
                    model.creator === TaskCreator.System ? t<string>(model.description ?? "") : model.description,
                }
              : undefined
          }
        />
      </ModalForm>
    </Card>
  );
};

export default AutomationTasks;
